import { z } from "@reflag/shared/utils/zod";

import { APIResponse } from "./api";
import { AttributeField } from "./attributeFilter";
import { AttributeValuesQuerySchema } from "./companyAPI";
import { EnvironmentSelectionQuerySchema } from "./environmentAPI";
import { ExternalIdSchema } from "./schemas/external";

export interface Event {
  name: string;
  attributes: { [key: string]: string };
  timestamp: string;
  user:
    | {
        id: string;
        name: string | null;
        email: string | null;
        avatarUrl: string | null;
        companies: {
          id: string;
          name: string | null;
          avatarUrl: string | null;
        }[];
      }
    | undefined;
}

export const DebugEventTypes = [
  "check",
  "check-config",
  "track",
  "feedback",
  "user",
  "company",
  "feedback",
  "unknown",
] as const;

export type DebugEventType = (typeof DebugEventTypes)[number];
export type DebugEventAttributeValue = string | unknown[];

export interface EvaluationError {
  code: string;
  field: string;
  operator?: string;
  message: string;
}

export function normalizeEvaluationErrors(
  errors: EvaluationError[] = [],
  legacyMissingFields: string[] = [],
): EvaluationError[] {
  if (errors.length > 0) return errors;

  return legacyMissingFields.map((field) => ({
    code: "MISSING_CONTEXT_FIELD",
    field,
    message: `Context field "${field}" is required to evaluate targeting rules.`,
  }));
}

export interface DebugProblem {
  id: string;
  code: string;
  field: string;
  operator?: string;
  lastSeen: string;
  count: number;
}

export interface DebugEvent {
  id: string;
  envId: string;
  timestamp: string;
  sdkVersion: string;
  serviceName: string;
  user?: {
    id: string;
    name?: string;
    email?: string;
    avatarUrl?: string;
    known: boolean;
  };
  company?: {
    id: string;
    name?: string;
    avatarUrl?: string;
    known: boolean;
    inferred: boolean;
  };
  debugEventType: DebugEventType;
  featureFlagEvent?: {
    featureKey: string;
    targetingVersion: number;
    evalResult: boolean | string;
    evalConfig?: {
      key: string;
      payload?: unknown;
    };
    evalRuleResults: unknown[];
    evalContext: Record<string, unknown>;
    evalErrors: EvaluationError[];
  };
  name?: string;
  attributes?: Record<string, DebugEventAttributeValue>;
  feedback?: {
    featureId: string;
    score?: number;
    comment?: string;
    question?: string;
    promptedQuestion?: string;
    promptId?: string;
    source?: string;
  };
  features: {
    id: string;
    name: string;
    icon: string | null;
    iconColor: string | null;
  }[];
}

export const LatestEventsQuerySchema = EnvironmentSelectionQuerySchema.extend({
  lastTS: z.string().optional(),
  eventName: z.string().optional(),
});

export const DebugEventsQuerySchema = EnvironmentSelectionQuerySchema.extend({
  lastTS: z.string().optional(),
  eventName: z.string().optional(),
  featureId: z.string().optional(),
  companyId: ExternalIdSchema.optional(),
  userId: ExternalIdSchema.optional(),
  eventTypes: z.array(z.enum(DebugEventTypes)).optional(),
  hasProblem: z.stringbool().optional(),
});

export const DebugProblemsQuerySchema = EnvironmentSelectionQuerySchema.extend({
  featureId: z.string().optional(),
  companyId: ExternalIdSchema.optional(),
  userId: ExternalIdSchema.optional(),
});

export const DebugProblemOccurrencesQuerySchema =
  DebugProblemsQuerySchema.extend({
    code: z.string().min(1),
    field: z.string().min(1),
    operator: z.string().optional(),
    timestamp: z.iso.datetime().optional(),
    eventId: z.uuid().optional(),
    direction: z.enum(["older", "newer", "exact"]).optional(),
  }).refine(
    ({ timestamp, eventId, direction }) =>
      [timestamp, eventId, direction].every((value) => value === undefined) ||
      [timestamp, eventId, direction].every((value) => value !== undefined),
    {
      message: "Timestamp, event ID, and direction must be supplied together.",
    },
  );

export interface DebugProblemOccurrence {
  problem: DebugProblem | null;
  affectedFlags: DebugEvent["features"];
  event: DebugEvent | null;
  hasOlder: boolean;
  hasNewer: boolean;
}

export type DebugProblemOccurrencesQueryType = z.input<
  typeof DebugProblemOccurrencesQuerySchema
>;

export type LatestEventsQueryType = z.input<typeof LatestEventsQuerySchema>;
export type DebugEventsQueryType = z.input<typeof DebugEventsQuerySchema>;
export type DebugProblemsQueryType = z.input<typeof DebugProblemsQuerySchema>;

export const EventAttributeValuesQuerySchema =
  EnvironmentSelectionQuerySchema.extend(AttributeValuesQuerySchema.shape);

type EventAttributeValuesQueryType = z.input<
  typeof EventAttributeValuesQuerySchema
>;

export const EventsByNameQuerySchema = EnvironmentSelectionQuerySchema;

export type EventsByNameQueryType = z.input<typeof EventsByNameQuerySchema>;

export interface EventAPI {
  "/apps/:appId/debug-events/problems/count": {
    GET: {
      response: APIResponse<{ count: number }>;
      params: { appId: string };
      query: z.input<typeof EnvironmentSelectionQuerySchema>;
    };
  };
  "/apps/:appId/debug-events/problems": {
    GET: {
      response: APIResponse<{ problems: DebugProblem[] }>;
      params: { appId: string };
      query: DebugProblemsQueryType;
    };
  };
  "/apps/:appId/debug-events/problems/occurrences": {
    GET: {
      response: APIResponse<DebugProblemOccurrence>;
      params: { appId: string };
      query: DebugProblemOccurrencesQueryType;
    };
  };
  "/apps/:appId/events/latest": {
    GET: {
      response: APIResponse<{
        events: Event[];
      }>;
      params: { appId: string };
      query: LatestEventsQueryType;
    };
  };
  "/apps/:appId/debug-events/latest": {
    GET: {
      response: APIResponse<{
        events: DebugEvent[];
      }>;
      params: { appId: string };
      query: DebugEventsQueryType;
    };
  };
  "/apps/:appId/events/by-name": {
    GET: {
      response: APIResponse<{
        events: string[];
      }>;
      params: { appId: string };
      query: EventsByNameQueryType;
    };
  };
  "/apps/:appId/events/by-name/:eventName/attributes": {
    GET: {
      response: APIResponse<{
        attributes: AttributeField[];
      }>;
      params: { appId: string; eventName: string };
      query: EventsByNameQueryType;
    };
  };
  "/apps/:appId/events/by-name/:eventName/attributes/:attribute/values": {
    GET: {
      response: APIResponse<{
        values: string[];
      }>;
      params: { appId: string; eventName: string; attribute: string };
      query: EventAttributeValuesQueryType;
    };
  };
}
